<template>
    <div v-if="isVisible" class="iso-preview-legend">
        <div class="iso-preview-legend-header">
            <div class="iso-preview-legend-title">
                <strong>{{ $t('ISO Preview') }}</strong>
                <div class="iso-preview-legend-actions">
                    <div v-if="!loading" class="iso-preview-legend-actions-content">
                        <Button v-if="!isoPreview.onlyPreviewAppMap"
                            icon="pi pi-upload"
                            class="pulse-button"
                            :severity="gridCoverage.severity === 'success' || gridCoverage.severity === 'warn' ? 'success' : 'secondary'"
                            size="large"
                            variant="text"
                            :disabled="gridCoverage.severity === 'error'"
                            @click="uploadISOData()"
                            v-tooltip.bottom="gridCoverageTooltip"
                        />
                        <Button
                            icon="pi pi-times"
                            severity="danger"
                            size="large"
                            variant="text"
                            @click="isoPreview.clearPreview(true)"
                            v-tooltip.bottom="!isoPreview.onlyPreviewAppMap ? $t('Cancel upload') : $t('Clear and close')"
                        />
                    </div>
                    <div v-else class="iso-preview-legend-actions-content">
                        <ProgressSpinner
                            style="width: 20px; height: 20px"
                            strokeWidth="4"
                            fill="#212121"
                            animationDuration=".5s"
                            aria-label="Custom ProgressSpinner"
                        />
                    </div>
                </div>
            </div>
            <div class="iso-preview-legend-subtitle">
                <Message :severity="gridCoverage.severity" class="iso-preview-legend-subtitle-value">
                    <span>
                        <i :class="gridCoverageIcon" style="font-size: 13px;"></i>
                        <span>{{ $t('The field is {coverage}% inside grid', { coverage: gridCoverage.displayPercent }) }}</span>
                    </span>
                </Message>
            </div>
        </div>

        <Divider class="m-0" />

        <div v-if="pointsKeyOptions.length > 1" class="iso-preview-legend-selector">
            <label class="iso-preview-legend-label">{{ $t('Layer') }}</label>
            <Select
                v-model="localSelectedPointsKey"
                :options="pointsKeyOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                size="small"
                @change="onPointsKeyChange"
            />
        </div>

        <div class="iso-preview-legend-row">
            <span class="iso-preview-legend-key">{{ $t('DDI name') }}:</span>
            <span class="iso-preview-legend-value">{{ ddiInfo.name }}</span>
        </div>
        <div class="iso-preview-legend-row">
            <span class="iso-preview-legend-key">{{ $t('DDI code') }}:</span>
            <span class="iso-preview-legend-value">{{ ddiInfo.number }}</span>
        </div>
        <div class="iso-preview-legend-row">
            <span class="iso-preview-legend-key">{{ $t('Unit') }}:</span>
            <span class="iso-preview-legend-value">{{ selectedPointsContainer?.unit || '-' }}</span>
        </div>
        <div class="iso-preview-legend-row">
            <span class="iso-preview-legend-key">{{ $t('Raster size') }}:</span>
            <span class="iso-preview-legend-value">{{ rasterSizeLabel.width }} x {{ rasterSizeLabel.height }}</span>
        </div>
        <div class="iso-preview-legend-row">
            <span class="iso-preview-legend-key">{{ $t('Zones number') }}:</span>
            <span class="iso-preview-legend-value">{{ zonesNumber }}</span>
        </div>

        <Divider class="m-0" />

        <div class="iso-preview-legend-swatches">
            <div
                v-for="row in legendRows"
                :key="row.key"
                class="iso-preview-legend-swatches-row"
            >
                <span class="iso-preview-legend-color" :style="{ backgroundColor: row.color }"></span>
                <span class="iso-preview-legend-range">{{ row.label }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { createDraggable } from 'animejs'
import { useIsoPreview } from '@/stores/isoPreview'
import { useTasksStore } from '@/stores/tasks'
import { useToast } from 'primevue/usetoast'
import { useRoute } from 'vue-router'

export default {
    name: 'IsoPreviewLegend',
    setup() {
        const isoPreview = useIsoPreview()
        const ts = useTasksStore()
        const toast = useToast()
        const route = useRoute()

        return {
            isoPreview,
            ts,
            toast,
            route,
        }
    },
    data() {
        return {
            localSelectedPointsKey: null,
            loading: false,
        }
    },
    beforeRouteLeave() {
        if (this.isoPreview.visible) {
            this.isoPreview.clearPreview(true)
        }
    },
    computed: {
        isVisible() {
            return this.isoPreview.visible && this.pointsCount > 0
        },
        selectedPointsContainer() {
            const pointsMap = this.isoPreview.previewPayload?.points || {}
            const key = this.isoPreview.selectedPointsKey || Object.keys(pointsMap)[0]
            return key ? pointsMap[key] : null
        },
        pointsCount() {
            return this.selectedPointsContainer?.stats?.count
                || this.selectedPointsContainer?.points?.length
                || 0
        },
        pointsKeyOptions() {
            return this.isoPreview.availablePointsKeys.map((key, index) => ({
                // Show user-friendly labels while keeping the technical key as value.
                // Example: type2_uint32_channel_2_points -> Layer 2
                label: `Layer ${this.extractLayerIndex(key, index)}`,
                value: key,
            }))
        },
        legendRows() {
            const points = this.selectedPointsContainer?.points
            if (!Array.isArray(points) || !points.length) {
                return []
            }

            const byValue = new Map()
            points.forEach((p) => {
                const key = p.value
                if (key === undefined || key === null) return
                if (!byValue.has(key)) {
                    byValue.set(key, p)
                }
            })

            return Array.from(byValue.entries())
                .sort((a, b) => Number(a[0]) - Number(b[0]))
                .map(([key, point]) => ({
                    key,
                    color: point.fill || '#888888',
                    label: `${key}`,
                }))
        },
        rasterSizeLabel() {
            const widthM = Number(this.selectedPointsContainer?.cell_size_m?.width_m)
            const heightM = Number(this.selectedPointsContainer?.cell_size_m?.height_m)
            if (!Number.isFinite(widthM) || !Number.isFinite(heightM)) {
                return { width: '2.50 m', height: '2.50 m' }
            }
            return { width: widthM.toFixed(2), height: heightM.toFixed(2) }
        },
        zonesNumber() {
            return this.selectedPointsContainer?.unique_values?.length || 0
        },
        ddiInfo() {
            return {
                number: this.selectedPointsContainer?.ddi?.number || '-',
                name: this.selectedPointsContainer?.ddi?.name || '-'
            }
        },
        gridCoverage() {
            const coverage = this.isoPreview.previewPayload?.grid_coverage
            if (!coverage) return '-'

            const percent = coverage.status === 'partial'
                ? Math.min(coverage.covered_percent, 99.99)
                : coverage.covered_percent

            const displayPercent = percent?.toFixed(2)

            // > 96% is success, === 96% is warning, < 96% is error
            return {
                displayPercent: displayPercent ? `${displayPercent}` : '-',
                severity: percent >= this.isoPreview.acceptedCoverage ? 'success' : percent === this.isoPreview.acceptedCoverage ? 'warn' : 'error'
            }
        },
        gridCoverageIcon() {
            if (this.gridCoverage.severity === 'success') return 'pi pi-check-circle'
            if (this.gridCoverage.severity === 'warn') return 'pi pi-exclamation-triangle'
            return 'pi pi-times-circle'
        },
        gridCoverageTooltip() {
            switch (this.gridCoverage.severity) {
                case 'success':
                case 'warn':
                    return this.$t('Upload previewed ISO data')
                case 'error':
                    return this.$t(
                        'Cannot upload this ISO data because the field coverage is below the required threshold (minimum {coverage}%)',
                        { coverage: this.isoPreview.acceptedCoverage }
                    )
            }
        }
    },
    watch: {
        'route.name': {
            immediate: true,
            handler(toName, fromName) {
                if (this.isoPreview.visible && toName !== fromName) {
                    this.isoPreview.clearPreview(true)
                }
            }
        },
        isVisible: {
            immediate: true,
            handler(visible) {
                if (visible) {
                    if (!this.isoPreview.legendDraggable) {
                        this.isoPreview.legendDraggable = createDraggable('.iso-preview-legend-control', {
                            container: '#map',
                            snap: 56,
                            dragThreshold: { mouse: 6, touch: 10 },
                        })
                    } else {
                        this.isoPreview.legendDraggable.enable()
                        this.isoPreview.legendDraggable.refresh()
                    }
                } else if (this.isoPreview.legendDraggable) {
                    this.isoPreview.legendDraggable.disable()
                }
            },
        },
        pointsCount() {
            if (this.isVisible && this.isoPreview.legendDraggable) {
                this.isoPreview.legendDraggable.refresh()
            }
        },
        'isoPreview.selectedPointsKey': {
            immediate: true,
            handler(value) {
                this.localSelectedPointsKey = value
                if (this.isVisible && this.isoPreview.legendDraggable) {
                    this.isoPreview.legendDraggable.refresh()
                }
            },
        },
    },
    methods: {
        extractLayerIndex(key, index) {
            const match = String(key).match(/channel_(\d+)_points/i)
            if (match && match[1]) {
                return Number(match[1])
            }
            return index + 1
        },
        onPointsKeyChange() {
            this.isoPreview.setSelectedPointsKey(this.localSelectedPointsKey)
        },
        async uploadISOData() {
            if (!this.isoPreview.isoData.taskId || !this.isoPreview.isoData.file) {
                return
            }
            this.loading = true
            const res = await this.ts.uploadISOData(this.isoPreview.isoData.taskId, this.isoPreview.isoData.file)
            if (!res?.ok) {
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to upload ISO Data',
                    life: 30000
                })
            } else {
                this.toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'ISO Data Uploaded',
                    life: 10000,
                })
                this.isoPreview.clearPreview(true)
                await this.updateTask()
            }
            this.loading = false
        },
        async updateTask() {
            const taskId = Number(this.route.params.taskId)

            if (taskId) {
                // that means we are on the TaskDetails page
                const task = await this.ts.getFieldTask(taskId)
                if (task?.id) {
                    this.ts.tasks[task.id] = task
                } else {
                    // something went wrong with the task, refresh the task list
                    await this.ts.getFieldTasksByPeriod(this.ts.field.id, this.ts.activeTasksPeriod)
                    this.toast.add({
                        severity: 'warn',
                        summary: this.$t('Warning'),
                        detail: this.$t('Could not refresh task info'),
                        life: 5000,
                    })
                }
            } else {
                // that means we are on the TasksList page
                await this.ts.getFieldTasksByPeriod(this.ts.field.id, this.ts.activeTasksPeriod)
            }
        }
    }
}
</script>

<style scoped>
.iso-preview-legend {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: #212121;
    color: #fff;
    min-width: 220px;
    max-width: 320px;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #313131;
}

.iso-preview-legend-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.iso-preview-legend-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-size: 1.25rem;
}

.iso-preview-legend-actions {
    display: flex;
    align-items: center;
}

.iso-preview-legend-actions-content {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
    min-height: 20px;
}

.iso-preview-legend-subtitle-value {
    margin: 0;
    font-size: 13px;
}

.iso-preview-legend-subtitle-value :deep(.p-message-content) {
    padding: 4px 6px;
    gap: 4px;
    justify-content: center;
    align-items: center;
}

.iso-preview-legend-subtitle-value :deep(.p-message-text) {
    font-size: 13px;
    line-height: 1;
    text-align: center;
}

.iso-preview-legend-subtitle-value :deep(.p-message-text > span) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.iso-preview-legend-subtitle-value :deep(.p-message-text > span i) {
    line-height: 1;
}

.iso-preview-legend-subtitle-value :deep(.p-message-text b) {
    font-size: 13px;
}

.iso-preview-legend-subtitle-value :deep(.p-message-icon) {
    font-size: 13px;
}

.iso-preview-legend-selector {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.iso-preview-legend-label {
    font-size: 12px;
    color: #c9c9c9;
}

.iso-preview-legend-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
}

.iso-preview-legend-key {
    font-size: 12px;
    color: #c9c9c9;
}

.iso-preview-legend-value {
    font-size: 12px;
    color: #ebebeb;
}

.iso-preview-legend-swatches {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.iso-preview-legend-swatches-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.iso-preview-legend-color {
    width: 14px;
    height: 14px;
    border-radius: 3px;
    border: 1px solid #1a1a1a;
    flex-shrink: 0;
}

.iso-preview-legend-range {
    font-size: 12px;
    color: #ebebeb;
}

:deep(.pulse-button.p-button:not(:disabled)) {
    animation: bgPulse 1s ease-in-out infinite;
    border-radius: 0.4rem;
}

:deep(.pulse-button.p-button.p-button-icon-only) {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0 !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

@keyframes bgPulse {
    0%, 100% {
        background-color: transparent;
        box-shadow: none;
    }
    50% {
        background-color: #22c55e8c;
        box-shadow:
            0 0 0 2px #22c55eb3,
            0 0 12px #22c55e66;
    }
}
</style>
